import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { IconButton } from '../components/IconButton'
import { Modal } from '../components/Modal'
import { SortableTh } from '../components/SortableTh'
import { useTableSort } from '../hooks/useTableSort'
import {
  defaultsPeriodoLetivo,
  fetchDiasLetivosSet,
  isDiaLetivo,
} from '../lib/calendarioLetivo'
import {
  formatMesReferencia,
  formatPeriodoConsumo,
  mesReferencia,
  quantidadeRestante,
} from '../lib/pedidoMensal'
import {
  arredondarKgParaEmbalagensFechadas,
  formatUnidadeExibicao,
  kgParaUnidadesComerciais,
  kgTemFracaoEmbalagem,
  opcoesArredondamentoEmbalagem,
  type DirecaoArredondamentoEmbalagem,
} from '../lib/produtoConversaoKg'
import { fetchConsumoPrevisto, fetchNecessidadePeriodo } from '../lib/supabaseQuery'
import { supabase } from '../lib/supabase'
import {
  downloadText,
  formatDate,
  formatNumber,
  monthBoundsISO,
  todayISO,
  toCsv,
} from '../lib/utils'
import type {
  AlimentoMacronutriente,
  CompraItem,
  Escola,
  NecessidadePeriodo,
  PlanejamentoEscola,
  PlanejamentoItem,
  PlanejamentoRota,
  Produto,
  Rota,
  StatusPlanejamento,
  Usuario,
} from '../types/database'

type PedidoOption = {
  id: string
  codigo: string
  numero: number
  mes_referencia: string | null
  periodo_inicio: string
  periodo_fim: string
  data_compra: string
  avulso: boolean
  itens?: Pick<CompraItem, 'alimento_id' | 'quantidade' | 'quantidade_alocada'>[]
}

type PlanoRow = PlanejamentoRota & {
  escolas?: (PlanejamentoEscola & {
    escola?: { nome: string }
    itens?: (PlanejamentoItem & {
      alimento?: { nome: string; categoria?: string; produto_id?: string | null }
    })[]
  })[]
}

type LineItemForm = {
  key: string
  alimento_id: string
  quantidade: string
}

type DraftSchool = {
  key: string
  escola_id: string
  itens: LineItemForm[]
}

function emptyLineItem(): LineItemForm {
  return {
    key: crypto.randomUUID(),
    alimento_id: '',
    quantidade: '',
  }
}

function buildDraftSchoolsForRota(escolas: Escola[], rotaId: string): DraftSchool[] {
  return escolas
    .filter((e) => e.rota_id === rotaId)
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
    .map((e) => ({
      key: crypto.randomUUID(),
      escola_id: e.id,
      itens: [],
    }))
}

function necessidadeToItens(
  rows: NecessidadePeriodo[],
  saldoPorAlimento?: Map<string, number>,
): LineItemForm[] {
  const saldo = saldoPorAlimento ? new Map(saldoPorAlimento) : null
  const itens: LineItemForm[] = []
  for (const n of rows) {
    let qtd = Number(n.quantidade) || 0
    if (saldo) {
      const disponivel = saldo.get(n.alimento_id) ?? 0
      qtd = Math.min(qtd, Math.max(0, disponivel))
      saldo.set(n.alimento_id, disponivel - qtd)
    }
    if (qtd <= 0) continue
    itens.push({
      key: crypto.randomUUID(),
      alimento_id: n.alimento_id,
      quantidade: String(qtd),
    })
  }
  return itens.length ? itens : [emptyLineItem()]
}

function agregarNecessidadePorAlimento(rows: NecessidadePeriodo[]): NecessidadePeriodo[] {
  const map = new Map<string, NecessidadePeriodo>()
  for (const row of rows) {
    const prev = map.get(row.alimento_id)
    if (!prev) {
      map.set(row.alimento_id, row)
      continue
    }
    map.set(row.alimento_id, {
      ...prev,
      quantidade: (Number(prev.quantidade) || 0) + (Number(row.quantidade) || 0),
      valor_total: (Number(prev.valor_total) || 0) + (Number(row.valor_total) || 0),
    })
  }
  return [...map.values()]
}

function mesRefFromIso(iso: string): string {
  const [y, m] = iso.split('-').map(Number)
  return mesReferencia(y, m)
}

function somarDraftPorAlimento(
  schools: DraftSchool[],
  exceptSchoolKey?: string,
): Map<string, number> {
  const map = new Map<string, number>()
  for (const school of schools) {
    if (exceptSchoolKey && school.key === exceptSchoolKey) continue
    for (const it of school.itens) {
      if (!it.alimento_id) continue
      const q = Number(it.quantidade) || 0
      if (q <= 0) continue
      map.set(it.alimento_id, (map.get(it.alimento_id) || 0) + q)
    }
  }
  return map
}

/** Um alimento por escola no banco — soma linhas repetidas no formulário. */
function consolidarItensEscola(itens: LineItemForm[]): LineItemForm[] {
  const porAlimento = new Map<string, number>()
  for (const it of itens) {
    if (!it.alimento_id) continue
    const q = Number(it.quantidade) || 0
    if (q <= 0) continue
    porAlimento.set(it.alimento_id, (porAlimento.get(it.alimento_id) || 0) + q)
  }
  if (!porAlimento.size) return [emptyLineItem()]
  return [...porAlimento.entries()].map(([alimento_id, quantidade]) => ({
    key: crypto.randomUUID(),
    alimento_id,
    quantidade: String(quantidade),
  }))
}

function escolaSemItensPreenchidos(school: DraftSchool) {
  return !school.itens.some((i) => i.alimento_id && Number(i.quantidade) > 0)
}

function statusBadgeClass(status: StatusPlanejamento) {
  switch (status) {
    case 'concluido':
      return 'badge badge-ok'
    case 'parcial':
      return 'badge badge-atencao'
    case 'cancelado':
      return 'badge badge-neutral'
    default:
      return 'badge badge-zerado'
  }
}

function statusLabel(status: StatusPlanejamento) {
  switch (status) {
    case 'planejado':
      return 'Planejado'
    case 'parcial':
      return 'Parcial'
    case 'concluido':
      return 'Concluído'
    case 'cancelado':
      return 'Cancelado'
  }
}

function formatQtdProdutoUn(
  qtdKg: number | string,
  produto: Produto | null | undefined,
): string {
  const kg = Number(qtdKg)
  if (!produto || !Number.isFinite(kg) || kg <= 0) return '—'
  const un = kgParaUnidadesComerciais(kg, produto)
  return un != null ? formatNumber(un, 3) : '—'
}

/** Ex.: "↓ 2 un (1 kg) · ↑ 3 un (1,5 kg)" — só quando há fração de embalagem. */
function formatAjusteEmbalagemSugerido(
  qtdKg: number | string,
  produto: Produto | null | undefined,
): string | null {
  const kg = Number(qtdKg)
  if (!produto || !Number.isFinite(kg) || kg <= 0) return null
  const op = opcoesArredondamentoEmbalagem(kg, produto)
  if (!op?.precisaEscolha) return null
  const unLabel = formatUnidadeExibicao(produto.unidade)
  return `↓ ${formatNumber(op.menos.un, 0)} ${unLabel} (${formatNumber(op.menos.kg, 3)} kg) · ↑ ${formatNumber(op.mais.un, 0)} ${unLabel} (${formatNumber(op.mais.kg, 3)} kg)`
}

function aplicarArredondamentoNaLinha(
  row: LineItemForm,
  produto: Produto | null | undefined,
  direcao: DirecaoArredondamentoEmbalagem,
): LineItemForm {
  const kg = Number(row.quantidade)
  if (!produto || !Number.isFinite(kg) || kg <= 0) return row
  if (!kgTemFracaoEmbalagem(kg, produto)) return row
  const novo = arredondarKgParaEmbalagensFechadas(kg, produto, direcao)
  return { ...row, quantidade: String(novo) }
}

function produtoDoAlimento(
  alimento: { produto_id?: string | null } | undefined,
  produtoById: Map<string, Produto>,
): Produto | undefined {
  if (!alimento?.produto_id) return undefined
  return produtoById.get(alimento.produto_id)
}

export function PlanejamentoRotaPage() {
  const { canWrite, canDelete, profile } = useAuth()
  const [planos, setPlanos] = useState<PlanoRow[]>([])
  const [rotas, setRotas] = useState<Rota[]>([])
  const [escolas, setEscolas] = useState<Escola[]>([])
  const [alimentos, setAlimentos] = useState<AlimentoMacronutriente[]>([])
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [filtroRota, setFiltroRota] = useState('')
  const [filtroInicio, setFiltroInicio] = useState(() => monthBoundsISO().inicio)
  const [filtroFim, setFiltroFim] = useState(() => monthBoundsISO().fim)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [detail, setDetail] = useState<PlanoRow | null>(null)
  const [expandedSchool, setExpandedSchool] = useState<string | null>(null)
  const [entregasPorParada, setEntregasPorParada] = useState<Set<string>>(() => new Set())
  const [alimentosCardapioIds, setAlimentosCardapioIds] = useState<Set<string>>(() => new Set())
  const [carregandoCardapio, setCarregandoCardapio] = useState(false)
  const [carregandoItens, setCarregandoItens] = useState(false)
  const [diasLetivos, setDiasLetivos] = useState<Set<string>>(() => new Set())
  const [sugerindoKey, setSugerindoKey] = useState<string | null>(null)
  const ultimoPeriodoItensRef = useRef('')
  const ultimaCompraPeriodoRef = useRef('')
  const cardapioSeqRef = useRef(0)
  const [saving, setSaving] = useState(false)

  const [rotaId, setRotaId] = useState('')
  const [compraId, setCompraId] = useState('')
  const [dataEntrega, setDataEntrega] = useState(todayISO())
  const [periodoInicio, setPeriodoInicio] = useState(todayISO())
  const [periodoFim, setPeriodoFim] = useState(todayISO())
  const [draftSchools, setDraftSchools] = useState<DraftSchool[]>([])
  const [filtroSemItens, setFiltroSemItens] = useState(false)
  const [erroSemItensAtivo, setErroSemItensAtivo] = useState(false)
  const [escolaParaAdd, setEscolaParaAdd] = useState('')
  const [pedidos, setPedidos] = useState<PedidoOption[]>([])
  const [logisticas, setLogisticas] = useState<Usuario[]>([])
  const [responsavelEntregaId, setResponsavelEntregaId] = useState('')

  const writable = canWrite('planejamento_rotas')
  const tableSort = useTableSort<PlanoRow>('data_entrega', 'desc')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const [p, r, esc, al, pr, ent, ped, log] = await Promise.all([
      supabase
        .from('planejamento_rotas')
        .select(
          '*, rota:rotas(nome), compra:compras(codigo, mes_referencia), responsavel_entrega:usuarios!planejamento_rotas_responsavel_entrega_id_fkey(nome, email), escolas:planejamento_escolas(*, escola:escolas(nome), itens:planejamento_itens(*, alimento:alimentos_macronutrientes(nome, categoria, produto_id)))',
        )
        .order('data_entrega', { ascending: false })
        .limit(300),
      supabase.from('rotas').select('*').order('nome'),
      supabase.from('escolas').select('*').order('nome'),
      supabase.from('alimentos_macronutrientes').select('*').order('nome'),
      supabase.from('produtos').select('*').order('nome'),
      supabase.from('entregas').select('planejamento_escola_id').not('planejamento_escola_id', 'is', null),
      supabase
        .from('compras')
        .select(
          'id, codigo, numero, mes_referencia, periodo_inicio, periodo_fim, data_compra, avulso, itens:compra_itens(alimento_id, quantidade, quantidade_alocada)',
        )
        .order('mes_referencia', { ascending: false })
        .order('numero', { ascending: false })
        .limit(200),
      supabase
        .from('usuarios')
        .select('*')
        .eq('perfil', 'logistica')
        .eq('ativo', true)
        .order('nome'),
    ])
    if (p.error || r.error || esc.error || al.error || pr.error || ent.error || ped.error || log.error) {
      setError(
        p.error?.message ||
          r.error?.message ||
          esc.error?.message ||
          al.error?.message ||
          pr.error?.message ||
          ent.error?.message ||
          ped.error?.message ||
          log.error?.message ||
          'Erro',
      )
    } else {
      const rows = ((p.data as PlanoRow[]) || []).map((row) => ({
        ...row,
        escolas: [...(row.escolas || [])].sort((a, b) => a.ordem - b.ordem),
      }))
      setPlanos(rows)
      setRotas((r.data as Rota[]) || [])
      setEscolas((esc.data as Escola[]) || [])
      setAlimentos((al.data as AlimentoMacronutriente[]) || [])
      setProdutos((pr.data as Produto[]) || [])
      setPedidos((ped.data as PedidoOption[]) || [])
      setLogisticas((log.data as Usuario[]) || [])
      setEntregasPorParada(
        new Set(
          ((ent.data as { planejamento_escola_id: string | null }[]) || [])
            .map((row) => row.planejamento_escola_id)
            .filter(Boolean) as string[],
        ),
      )
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    document.title = 'Planejamento de rota · ISEN EDUCAE'
    load()
  }, [load])

  const escolasDaRota = useMemo(
    () => escolas.filter((e) => e.rota_id === rotaId),
    [escolas, rotaId],
  )

  const escolasDisponiveis = useMemo(() => {
    const used = new Set(draftSchools.map((d) => d.escola_id))
    return escolasDaRota.filter((e) => !used.has(e.id))
  }, [escolasDaRota, draftSchools])

  const filtered = useMemo(
    () =>
      planos.filter((p) => {
        if (filtroRota && p.rota_id !== filtroRota) return false
        if (filtroInicio && p.data_entrega < filtroInicio) return false
        if (filtroFim && p.data_entrega > filtroFim) return false
        return true
      }),
    [planos, filtroRota, filtroInicio, filtroFim],
  )

  const filteredSorted = useMemo(() => {
    const accessors: Record<string, (p: PlanoRow) => string | number> = {
      data_entrega: (p) => p.data_entrega,
      rota: (p) => p.rota?.nome || '',
      pedido: (p) => p.compra?.codigo || '',
      responsavel: (p) => p.responsavel_entrega?.nome || '',
      status: (p) => p.status,
      escolas: (p) => p.escolas?.length || 0,
    }
    return tableSort.sortRows(filtered, accessors)
  }, [filtered, tableSort])

  const pedidosDoMes = useMemo(() => {
    const mes = dataEntrega ? mesRefFromIso(dataEntrega) : ''
    const list = pedidos.filter((p) => {
      if (compraId && p.id === compraId) return true
      if (!mes) return true
      return p.mes_referencia === mes
    })
    return [...list].sort((a, b) => {
      const cmpMes = (b.mes_referencia || '').localeCompare(a.mes_referencia || '')
      if (cmpMes !== 0) return cmpMes
      return (b.numero || 0) - (a.numero || 0)
    })
  }, [compraId, dataEntrega, pedidos])

  const pedidoSelecionado = useMemo(
    () => pedidos.find((p) => p.id === compraId) || null,
    [compraId, pedidos],
  )

  /** Saldo disponível no pedido, devolvendo a alocação do plano em edição. */
  const saldoPedidoDisponivel = useMemo(() => {
    const map = new Map<string, number>()
    if (!pedidoSelecionado?.itens) return map
    for (const item of pedidoSelecionado.itens) {
      map.set(item.alimento_id, quantidadeRestante(item))
    }
    if (editingId && compraId) {
      const planoAtual = planos.find((p) => p.id === editingId)
      if (planoAtual?.compra_id === compraId) {
        for (const esc of planoAtual.escolas || []) {
          for (const it of esc.itens || []) {
            map.set(it.alimento_id, (map.get(it.alimento_id) || 0) + Number(it.quantidade))
          }
        }
      }
    }
    return map
  }, [compraId, editingId, pedidoSelecionado, planos])

  function saldoParaSugestao(exceptSchoolKey?: string): Map<string, number> | undefined {
    if (!compraId) return undefined
    const saldo = new Map(saldoPedidoDisponivel)
    const jaNoDraft = somarDraftPorAlimento(draftSchools, exceptSchoolKey)
    for (const [alimentoId, qtd] of jaNoDraft) {
      saldo.set(alimentoId, Math.max(0, (saldo.get(alimentoId) || 0) - qtd))
    }
    return saldo
  }

  const produtoById = useMemo(() => new Map(produtos.map((p) => [p.id, p])), [produtos])

  const alimentosDoCardapio = useMemo(() => {
    const ids = new Set(alimentosCardapioIds)
    for (const school of draftSchools) {
      for (const item of school.itens) {
        if (item.alimento_id) ids.add(item.alimento_id)
      }
    }
    const list = alimentos.filter((a) => ids.has(a.id))
    return [...list].sort((a, b) => {
      const cmpCat = (a.categoria || '').localeCompare(b.categoria || '', 'pt-BR')
      if (cmpCat !== 0) return cmpCat
      const nomeA = produtoById.get(a.produto_id || '')?.nome || a.nome
      const nomeB = produtoById.get(b.produto_id || '')?.nome || b.nome
      return nomeA.localeCompare(nomeB, 'pt-BR')
    })
  }, [draftSchools, alimentos, alimentosCardapioIds, produtoById])

  const itensComFracaoEmbalagem = useMemo(() => {
    const nomes: string[] = []
    for (const school of draftSchools) {
      for (const item of school.itens) {
        if (!item.alimento_id || !(Number(item.quantidade) > 0)) continue
        const alimento = alimentos.find((a) => a.id === item.alimento_id)
        const produto = produtoDoAlimento(alimento, produtoById)
        if (!kgTemFracaoEmbalagem(Number(item.quantidade), produto)) continue
        const nome = produto?.nome || alimento?.nome || item.alimento_id
        if (!nomes.includes(nome)) nomes.push(nome)
      }
    }
    return nomes
  }, [draftSchools, alimentos, produtoById])

  const loadDiasLetivos = useCallback(async () => {
    const y = new Date().getFullYear()
    const set = await fetchDiasLetivosSet(y, y + 1)
    setDiasLetivos(set)
    return set
  }, [])

  function erroDataEntregaLetiva(iso: string, letivos: Set<string>) {
    if (!letivos.size) {
      return 'Cadastre dias letivos no Calendário antes de agendar entregas.'
    }
    if (!isDiaLetivo(iso, letivos)) {
      return 'A data da entrega deve ser um dia letivo cadastrado no Calendário.'
    }
    return null
  }

  const entregaForaLetivo = useMemo(
    () => (dataEntrega ? erroDataEntregaLetiva(dataEntrega, diasLetivos) : null),
    [dataEntrega, diasLetivos],
  )

  const loadAlimentosCardapio = useCallback(
    async (inicio: string, fim: string, rotaFiltro?: string) => {
      if (!inicio || !fim) {
        cardapioSeqRef.current++
        setAlimentosCardapioIds(new Set())
        return
      }
      setCarregandoCardapio(true)
      const seq = ++cardapioSeqRef.current
      const { data, error: err } = await fetchConsumoPrevisto({
        inicio,
        fim,
        rotaId: rotaFiltro || undefined,
      })
      if (seq !== cardapioSeqRef.current) return
      setCarregandoCardapio(false)
      if (err) {
        setError(err)
        setAlimentosCardapioIds(new Set())
        return
      }
      setAlimentosCardapioIds(new Set(data.map((row) => row.alimento_id)))
    },
    [],
  )

  const aplicarNecessidadeNasEscolas = useCallback(
    async (schoolKeys: Set<string>, forcarTodas: boolean) => {
      if (!rotaId || !periodoInicio || !periodoFim || periodoInicio > periodoFim) return

      setCarregandoItens(true)
      setError(null)
      const { data, error: err } = await fetchNecessidadePeriodo({
        inicio: periodoInicio,
        fim: periodoFim,
        rotaId,
      })
      setCarregandoItens(false)

      if (err) {
        setError(err)
        return
      }

      if (data.length) {
        setAlimentosCardapioIds((prev) => {
          const next = new Set(prev)
          for (const row of data) next.add(row.alimento_id)
          return next
        })
      }

      const byEscola = new Map<string, NecessidadePeriodo[]>()
      for (const row of data) {
        const list = byEscola.get(row.escola_id) || []
        list.push(row)
        byEscola.set(row.escola_id, list)
      }

      const resultado = {
        escolasComCardapio: 0,
        escolasComItensPositivos: 0,
        escolasIgnoradas: 0,
      }
      setDraftSchools((prev) => {
        const saldoBase = new Map(saldoPedidoDisponivel)
        if (compraId) {
          for (const s of prev) {
            if (schoolKeys.has(s.key) && (forcarTodas || escolaSemItensPreenchidos(s))) continue
            for (const it of s.itens) {
              if (!it.alimento_id) continue
              const q = Number(it.quantidade) || 0
              if (q <= 0) continue
              saldoBase.set(it.alimento_id, Math.max(0, (saldoBase.get(it.alimento_id) || 0) - q))
            }
          }
        }
        const running = compraId ? saldoBase : null
        return prev.map((s) => {
          if (!schoolKeys.has(s.key)) return s
          if (!forcarTodas && !escolaSemItensPreenchidos(s)) {
            resultado.escolasIgnoradas++
            return s
          }
          const rows = byEscola.get(s.escola_id) || []
          if (!rows.length) {
            return { ...s, itens: s.itens.length ? s.itens : [emptyLineItem()] }
          }
          resultado.escolasComCardapio++
          const itens = necessidadeToItens(rows, running || undefined)
          if (itens.some((i) => i.alimento_id && Number(i.quantidade) > 0)) {
            resultado.escolasComItensPositivos++
          }
          return { ...s, itens }
        })
      })

      const totalQtd = data.reduce((s, r) => s + (Number(r.quantidade) || 0), 0)
      const saldoPedido = [...saldoPedidoDisponivel.values()].reduce((s, q) => s + q, 0)
      if (!data.length) {
        setError(
          'Nenhum alimento do cardápio para o período e rota selecionados. Confira se o Calendário tem semanas de cardápio aplicadas nesse intervalo e se o período cobre o mês do pedido.',
        )
      } else if (totalQtd <= 0) {
        setError(
          'O cardápio existe no período, mas a necessidade das escolas da rota é zero. Verifique se o cardápio e a ficha técnica cobrem as modalidades/turnos das escolas (ex.: creche, pré, fundamental) e se há matrículas compatíveis.',
        )
      } else if (resultado.escolasComItensPositivos === 0 && resultado.escolasIgnoradas === 0) {
        if (compraId && saldoPedido <= 0) {
          setError('O pedido selecionado não tem saldo disponível para alocação nesta rota.')
        } else if (compraId) {
          setError(
            'Há necessidade no cardápio, mas o saldo do pedido não cobre as escolas da rota no período. Revise quantidades do pedido ou distribua manualmente por escola.',
          )
        } else if (resultado.escolasComCardapio === 0) {
          setError(
            'Nenhuma necessidade calculada para as escolas da rota. Confira matrículas e se o cardápio cobre as modalidades dessas escolas.',
          )
        }
      }
    },
    [compraId, periodoFim, periodoInicio, rotaId, saldoPedidoDisponivel],
  )

  function handleRotaChange(nextRotaId: string) {
    setRotaId(nextRotaId)
    setEscolaParaAdd('')
    setDraftSchools(nextRotaId ? buildDraftSchoolsForRota(escolas, nextRotaId) : [])
    setFiltroSemItens(false)
    setErroSemItensAtivo(false)
    setError(null)
  }

  const escolasSemItens = useMemo(
    () => draftSchools.filter(escolaSemItensPreenchidos),
    [draftSchools],
  )

  const draftVisivel = useMemo(
    () => (filtroSemItens ? escolasSemItens : draftSchools),
    [draftSchools, escolasSemItens, filtroSemItens],
  )

  function formatarNomesEscolasSemItens(schools: DraftSchool[], limite = 5) {
    const nomes = schools.map((s) => escolaNome(s.escola_id))
    if (nomes.length <= limite) return nomes.join(', ')
    const resto = nomes.length - limite
    return `${nomes.slice(0, limite).join(', ')} e mais ${resto}`
  }

  function removerTodasSemItens() {
    setDraftSchools((prev) => prev.filter((s) => !escolaSemItensPreenchidos(s)))
    setFiltroSemItens(false)
    setErroSemItensAtivo(false)
    setError(null)
  }

  useEffect(() => {
    if (filtroSemItens && escolasSemItens.length === 0) setFiltroSemItens(false)
  }, [escolasSemItens.length, filtroSemItens])

  const draftEscolasKey = useMemo(
    () => draftSchools.map((s) => `${s.key}:${s.escola_id}`).join('|'),
    [draftSchools],
  )

  useEffect(() => {
    if (!modalOpen || editingId || !compraId || !pedidoSelecionado) return
    if (ultimaCompraPeriodoRef.current === compraId) return
    ultimaCompraPeriodoRef.current = compraId
    const ini = String(pedidoSelecionado.periodo_inicio || pedidoSelecionado.mes_referencia || '').slice(
      0,
      10,
    )
    const fim = String(pedidoSelecionado.periodo_fim || ini).slice(0, 10)
    if (ini) setPeriodoInicio(ini)
    if (fim) setPeriodoFim(fim)
    ultimoPeriodoItensRef.current = ''
  }, [compraId, editingId, modalOpen, pedidoSelecionado])

  const periodoPlanejamentoForaDoPedido = useMemo(() => {
    if (!pedidoSelecionado || !periodoInicio || !periodoFim) return false
    const pIni = String(pedidoSelecionado.periodo_inicio).slice(0, 10)
    const pFim = String(pedidoSelecionado.periodo_fim).slice(0, 10)
    if (!pIni || !pFim) return false
    return periodoInicio < pIni || periodoFim > pFim
  }, [pedidoSelecionado, periodoFim, periodoInicio])

  useEffect(() => {
    if (!modalOpen || !periodoInicio || !periodoFim) return
    // Evita 2 RPCs pesadas em paralelo (consumo + necessidade): no fluxo com pedido/rota
    // a necessidade_periodo já preenche alimentosCardapioIds.
    if (rotaId && compraId && draftSchools.length > 0 && !editingId) return
    void loadAlimentosCardapio(periodoInicio, periodoFim, rotaId || undefined)
  }, [
    compraId,
    draftSchools.length,
    editingId,
    loadAlimentosCardapio,
    modalOpen,
    periodoFim,
    periodoInicio,
    rotaId,
  ])

  useEffect(() => {
    if (!modalOpen || editingId || !rotaId || !compraId || !periodoInicio || !periodoFim) return
    if (periodoInicio > periodoFim || !draftSchools.length) return

    const periodoKey = `${periodoInicio}|${periodoFim}|${compraId}`
    const forcarTodas = ultimoPeriodoItensRef.current !== periodoKey
    ultimoPeriodoItensRef.current = periodoKey

    const keys = new Set(draftSchools.map((s) => s.key))
    void aplicarNecessidadeNasEscolas(keys, forcarTodas)
  }, [
    aplicarNecessidadeNasEscolas,
    compraId,
    draftEscolasKey,
    draftSchools.length,
    editingId,
    modalOpen,
    periodoFim,
    periodoInicio,
    rotaId,
  ])

  useEffect(() => {
    if (!modalOpen) return
    void loadDiasLetivos().catch((e) => {
      setError(e instanceof Error ? e.message : 'Erro ao carregar calendário letivo')
    })
  }, [loadDiasLetivos, modalOpen])

  function planoTemEntregas(p: PlanoRow) {
    return (p.escolas || []).some((e) => entregasPorParada.has(e.id))
  }

  function podeEditarPlano(p: PlanoRow) {
    return writable && p.status === 'planejado' && !planoTemEntregas(p)
  }

  function closeModal() {
    setModalOpen(false)
    setEditingId(null)
    setCompraId('')
    setResponsavelEntregaId('')
    setError(null)
    setFiltroSemItens(false)
    setErroSemItensAtivo(false)
    ultimoPeriodoItensRef.current = ''
    ultimaCompraPeriodoRef.current = ''
  }

  async function openCreate() {
    setError(null)
    setErroSemItensAtivo(false)
    setFiltroSemItens(false)
    setEditingId(null)
    setRotaId('')
    setCompraId('')
    setResponsavelEntregaId(
      profile?.perfil === 'logistica' ? profile.id : '',
    )
    setEscolaParaAdd('')
    setDraftSchools([])
    try {
      const letivos = await loadDiasLetivos()
      const defs = defaultsPeriodoLetivo(letivos)
      setDataEntrega(defs.entrega)
      setPeriodoInicio(defs.inicio)
      setPeriodoFim(defs.fim)
    } catch (e) {
      const hoje = todayISO()
      setDataEntrega(hoje)
      setPeriodoInicio(hoje)
      setPeriodoFim(hoje)
      setError(e instanceof Error ? e.message : 'Erro ao carregar calendário letivo')
    }
    setModalOpen(true)
  }

  function openEdit(p: PlanoRow) {
    if (!podeEditarPlano(p)) return
    setError(null)
    setErroSemItensAtivo(false)
    setFiltroSemItens(false)
    setEditingId(p.id)
    setRotaId(p.rota_id)
    setCompraId(p.compra_id || '')
    setResponsavelEntregaId(p.responsavel_entrega_id || '')
    setDataEntrega(p.data_entrega)
    setPeriodoInicio(p.periodo_inicio || todayISO())
    setPeriodoFim(p.periodo_fim || todayISO())
    setDraftSchools(
      (p.escolas || []).map((esc) => ({
        key: esc.id,
        escola_id: esc.escola_id,
        itens:
          esc.itens && esc.itens.length
            ? esc.itens.map((it) => ({
                key: it.id,
                alimento_id: it.alimento_id,
                quantidade: String(it.quantidade),
              }))
            : [emptyLineItem()],
      })),
    )
    setEscolaParaAdd('')
    setModalOpen(true)
  }

  function addSchool() {
    if (!escolaParaAdd) return
    setDraftSchools((prev) => [
      ...prev,
      { key: crypto.randomUUID(), escola_id: escolaParaAdd, itens: [] },
    ])
    setEscolaParaAdd('')
  }

  function removeSchool(key: string) {
    setDraftSchools((prev) => prev.filter((d) => d.key !== key))
  }

  function moveSchool(key: string, dir: -1 | 1) {
    setDraftSchools((prev) => {
      const idx = prev.findIndex((d) => d.key === key)
      if (idx < 0) return prev
      const next = idx + dir
      if (next < 0 || next >= prev.length) return prev
      const copy = [...prev]
      const [row] = copy.splice(idx, 1)
      copy.splice(next, 0, row)
      return copy
    })
  }

  function updateItem(schoolKey: string, itemKey: string, patch: Partial<LineItemForm>) {
    setDraftSchools((prev) =>
      prev.map((s) =>
        s.key !== schoolKey
          ? s
          : {
              ...s,
              itens: s.itens.map((i) => (i.key === itemKey ? { ...i, ...patch } : i)),
            },
      ),
    )
  }

  function addItem(schoolKey: string) {
    setDraftSchools((prev) =>
      prev.map((s) => (s.key === schoolKey ? { ...s, itens: [...s.itens, emptyLineItem()] } : s)),
    )
  }

  function removeItem(schoolKey: string, itemKey: string) {
    setDraftSchools((prev) =>
      prev.map((s) =>
        s.key !== schoolKey
          ? s
          : {
              ...s,
              itens: s.itens.length <= 1 ? s.itens : s.itens.filter((i) => i.key !== itemKey),
            },
      ),
    )
  }

  async function sugerirItens(schoolKey: string) {
    const school = draftSchools.find((d) => d.key === schoolKey)
    if (!school || !rotaId) {
      setError('Selecione rota e escola para sugerir itens')
      return
    }
    if (!periodoInicio || !periodoFim || periodoInicio > periodoFim) {
      setError('Informe período início e fim válidos antes de sugerir itens')
      return
    }
    setSugerindoKey(schoolKey)
    setError(null)
    const { data, error: err } = await fetchNecessidadePeriodo({
      inicio: periodoInicio,
      fim: periodoFim,
      escolaId: school.escola_id,
    })
    setSugerindoKey(null)
    if (err) {
      setError(err)
      return
    }
    if (!data.length) {
      setError(
        `Nenhuma necessidade para ${escolaNome(school.escola_id)} no período. Verifique Calendário, Cardápio e matrículas.`,
      )
      return
    }

    const agregado = agregarNecessidadePorAlimento(data)
    const necessidadeTotal = agregado.reduce((s, r) => s + (Number(r.quantidade) || 0), 0)
    if (necessidadeTotal <= 0) {
      setError(
        `O cardápio não gera necessidade para ${escolaNome(school.escola_id)} — confira modalidades/turnos da escola e se a ficha técnica cobre o cardápio do período.`,
      )
      return
    }

    setAlimentosCardapioIds((prev) => {
      const next = new Set(prev)
      for (const row of agregado) next.add(row.alimento_id)
      return next
    })
    const saldo = saldoParaSugestao(schoolKey)
    const itens = necessidadeToItens(agregado, saldo)
    const itensValidos = itens.filter((i) => i.alimento_id && Number(i.quantidade) > 0)
    if (!itensValidos.length) {
      if (compraId) {
        setError(
          `A necessidade de ${escolaNome(school.escola_id)} excede o saldo restante do pedido. Libere quantidade em outras escolas ou ajuste o pedido.`,
        )
      } else {
        setError('Não foi possível montar itens sugeridos para esta escola.')
      }
      return
    }

    setDraftSchools((prev) =>
      prev.map((s) => (s.key !== schoolKey ? s : { ...s, itens: itensValidos })),
    )
  }

  /**
   * Regrava escolas e itens numa única transação no banco.
   * Antes isso era feito escola a escola pelo cliente: falha no meio da edição
   * apagava as escolas antigas e deixava só parte das novas, sem volta.
   */
  async function persistEscolasItens(planejamentoId: string, schools: DraftSchool[]) {
    const payload = schools.map((draft, i) => ({
      escola_id: draft.escola_id,
      ordem: i + 1,
      itens: consolidarItensEscola(draft.itens).map((it) => ({
        alimento_id: it.alimento_id,
        quantidade: Number(it.quantidade),
      })),
    }))

    const { error } = await supabase.rpc('salvar_planejamento_escolas', {
      p_planejamento_id: planejamentoId,
      p_escolas: payload,
    })
    if (error) throw new Error(error.message)
  }

  async function save() {
    if (!profile) return
    const errEntrega = erroDataEntregaLetiva(dataEntrega, diasLetivos)
    if (errEntrega) {
      setError(errEntrega)
      return
    }
    if (!rotaId || !dataEntrega) {
      setError('Informe rota e data da entrega')
      return
    }
    if (!compraId) {
      setError('Selecione o Pedido Mensal (código número.mês.ano)')
      return
    }
    if (!responsavelEntregaId) {
      setError('Selecione o responsável pela entrega (perfil Logística)')
      return
    }
    if (!draftSchools.length) {
      setErroSemItensAtivo(false)
      setError('Adicione ao menos uma escola na sequência')
      return
    }

    const vazias = draftSchools.filter(escolaSemItensPreenchidos)
    if (vazias.length) {
      const n = vazias.length
      setErroSemItensAtivo(true)
      setError(
        `${n} escola(s) sem itens (${formatarNomesEscolasSemItens(vazias)}). Remova-as da sequência ou preencha os alimentos.`,
      )
      return
    }

    for (const draft of draftSchools) {
      const itensValidos = consolidarItensEscola(draft.itens).filter(
        (it) => it.alimento_id && Number(it.quantidade) > 0,
      )
      const foraCardapio = itensValidos.filter((it) => !alimentosCardapioIds.has(it.alimento_id))
      if (alimentosCardapioIds.size > 0 && foraCardapio.length) {
        setErroSemItensAtivo(false)
        setError('Há alimentos fora do cardápio do período selecionado')
        return
      }
    }

    const totalDraft = somarDraftPorAlimento(draftSchools)
    for (const [alimentoId, qtd] of totalDraft) {
      const disponivel = saldoPedidoDisponivel.get(alimentoId) ?? 0
      if (qtd > disponivel + 1e-9) {
        const nome = alimentos.find((a) => a.id === alimentoId)?.nome || alimentoId
        setErroSemItensAtivo(false)
        setError(
          `Quantidade de «${nome}» (${formatNumber(qtd, 3)}) excede o saldo restante do pedido ${pedidoSelecionado?.codigo || ''} (${formatNumber(disponivel, 3)}).`,
        )
        return
      }
    }

    setSaving(true)
    setErroSemItensAtivo(false)
    setError(null)

    // Garante embalagem fechada (padrão: para mais) antes de gravar
    const schoolsParaSalvar = draftSchools.map((s) => ({
      ...s,
      itens: consolidarItensEscola(s.itens).map((row) => {
        const alimento = alimentos.find((a) => a.id === row.alimento_id)
        const produto = produtoDoAlimento(alimento, produtoById)
        return aplicarArredondamentoNaLinha(row, produto, 'mais')
      }),
    }))

    try {
      let planejamentoId = editingId
      if (editingId) {
        const { error: errUpd } = await supabase
          .from('planejamento_rotas')
          .update({
            rota_id: rotaId,
            data_entrega: dataEntrega,
            periodo_inicio: periodoInicio || null,
            periodo_fim: periodoFim || null,
            compra_id: compraId,
            responsavel_entrega_id: responsavelEntregaId,
          })
          .eq('id', editingId)

        if (errUpd) throw new Error(errUpd.message)

        // A limpeza das escolas antigas faz parte da transação da RPC.
        await persistEscolasItens(editingId, schoolsParaSalvar)
      } else {
        const { data: created, error: errPlan } = await supabase
          .from('planejamento_rotas')
          .insert({
            rota_id: rotaId,
            data_entrega: dataEntrega,
            status: 'planejado',
            periodo_inicio: periodoInicio || null,
            periodo_fim: periodoFim || null,
            compra_id: compraId,
            responsavel_entrega_id: responsavelEntregaId,
            criado_por: profile.id,
          })
          .select('id')
          .single()

        if (errPlan || !created) throw new Error(errPlan?.message || 'Erro ao criar planejamento')
        planejamentoId = created.id

        try {
          await persistEscolasItens(created.id, schoolsParaSalvar)
        } catch (e) {
          await supabase.from('planejamento_rotas').delete().eq('id', created.id)
          throw e
        }
      }

      const { error: errAloc } = await supabase.rpc('alocar_pedido_no_planejamento', {
        p_planejamento_id: planejamentoId,
        p_compra_id: compraId,
      })
      if (errAloc) throw new Error(errAloc.message)

      const { error: errTotais } = await supabase.rpc('recalcular_totais_pedido_previsto', {
        p_compra_id: compraId,
      })
      if (errTotais) throw new Error(errTotais.message)

      setSaving(false)
      closeModal()
      load()
    } catch (e) {
      setSaving(false)
      setError(e instanceof Error ? e.message : 'Erro ao salvar planejamento')
    }
  }

  async function remove(id: string) {
    if (!confirm('Excluir este planejamento? Só é possível se ainda não houver entregas vinculadas.')) {
      return
    }
    const plano = planos.find((p) => p.id === id)
    const compraAntiga = plano?.compra_id
    const { error: err } = await supabase.from('planejamento_rotas').delete().eq('id', id)
    if (err) setError(err.message)
    else {
      if (compraAntiga) {
        await supabase.rpc('recalcular_alocacao_pedido', { p_compra_id: compraAntiga })
      }
      if (detail?.id === id) setDetail(null)
      load()
    }
  }

  function exportCsv() {
    downloadText(
      'planejamento-rotas.csv',
      toCsv(
        filtered.map((p) => ({
          Data: p.data_entrega,
          Rota: p.rota?.nome,
          Pedido: p.compra?.codigo || '',
          Responsável: p.responsavel_entrega?.nome || '',
          Status: statusLabel(p.status),
          Escolas: p.escolas?.length || 0,
          'Período início': p.periodo_inicio,
          'Período fim': p.periodo_fim,
        })),
      ),
    )
  }

  function escolaNome(id: string) {
    return escolas.find((e) => e.id === id)?.nome || '—'
  }

  function escolaTotalAlunos(id: string) {
    return escolas.find((e) => e.id === id)?.total_alunos ?? 0
  }

  if (loading) return <div className="loading">Carregando planejamentos…</div>

  return (
    <div>
      <div className="page-header">
        <h1>Planejamento de rota</h1>
        <div className="page-actions">
          <button type="button" className="btn btn-secondary" onClick={exportCsv}>
            Exportar CSV
          </button>
          {writable && (
            <button type="button" className="btn btn-primary" onClick={() => void openCreate()}>
              Criar Rota
            </button>
          )}
        </div>
      </div>
      {error && !modalOpen && <div className="alert alert-error">{error}</div>}

      <div className="filters">
        <div className="field">
          <label>Início</label>
          <input type="date" value={filtroInicio} onChange={(e) => setFiltroInicio(e.target.value)} />
        </div>
        <div className="field">
          <label>Fim</label>
          <input type="date" value={filtroFim} onChange={(e) => setFiltroFim(e.target.value)} />
        </div>
        <div className="field">
          <label>Rota</label>
          <select value={filtroRota} onChange={(e) => setFiltroRota(e.target.value)}>
            <option value="">Todas</option>
            {rotas.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          style={{ alignSelf: 'flex-end' }}
          onClick={() => {
            const b = monthBoundsISO()
            setFiltroInicio(b.inicio)
            setFiltroFim(b.fim)
          }}
        >
          Mês atual
        </button>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh
                label="Data"
                sortKey="data_entrega"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="date"
              />
              <SortableTh
                label="Rota"
                sortKey="rota"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="text"
              />
              <SortableTh
                label="Pedido"
                sortKey="pedido"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="text"
              />
              <SortableTh
                label="Responsável"
                sortKey="responsavel"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="text"
              />
              <SortableTh
                label="Status"
                sortKey="status"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="status"
              />
              <SortableTh
                label="Escolas"
                sortKey="escolas"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="num"
              />
              <th className="col-actions">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredSorted.map((p) => (
              <tr key={p.id}>
                <td className="col-date">{formatDate(p.data_entrega)}</td>
                <td className="col-text">{p.rota?.nome || '—'}</td>
                <td className="col-text">{p.compra?.codigo || '—'}</td>
                <td className="col-text">{p.responsavel_entrega?.nome || '—'}</td>
                <td className="col-status">
                  <span className={statusBadgeClass(p.status)}>{statusLabel(p.status)}</span>
                </td>
                <td className="col-num">{p.escolas?.length || 0}</td>
                <td className="col-actions">
                  <div className="table-actions">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDetail(p)}>
                      Abrir
                    </button>
                    {podeEditarPlano(p) && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(p)}>
                        Editar
                      </button>
                    )}
                    {canDelete('planejamento_rotas') && p.status === 'planejado' && (
                      <IconButton
                        variant="delete"
                        label={`Excluir planejamento ${p.rota?.nome || ''}`}
                        onClick={() => remove(p.id)}
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!filteredSorted.length && (
              <tr>
                <td colSpan={7} className="kpi-sub">
                  Nenhum planejamento no período.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        title={editingId ? 'Editar planejamento de rota' : 'Criar planejamento de rota'}
        size="xl"
        onClose={closeModal}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={closeModal}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || !!entregaForaLetivo}
              onClick={() => void save()}
            >
              {saving ? 'Salvando…' : editingId ? 'Salvar alterações' : 'Salvar'}
            </button>
          </>
        }
      >
        {error && modalOpen && (
          <div className="alert alert-error">
            <div>{error}</div>
            {erroSemItensAtivo && escolasSemItens.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={removerTodasSemItens}
                >
                  Remover todas sem itens
                </button>
              </div>
            )}
          </div>
        )}
        <p className="kpi-sub" style={{ marginBottom: 12 }}>
          Alimentos manuais limitados ao cardápio do período
          {carregandoCardapio
            ? ' (carregando…)'
            : alimentosCardapioIds.size
              ? ` (${alimentosCardapioIds.size} itens)`
              : ' (nenhum item no período — verifique Calendário e Cardápio)'}
        </p>
        <div className="form-grid">
          <div className="field">
            <label>Rota *</label>
            <select value={rotaId} onChange={(e) => handleRotaChange(e.target.value)}>
              <option value="">Selecione</option>
              {rotas.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nome}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Pedido Mensal *</label>
            <select value={compraId} onChange={(e) => setCompraId(e.target.value)}>
              <option value="">Selecione o código</option>
              {pedidosDoMes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.codigo}
                  {p.periodo_inicio && p.periodo_fim
                    ? ` · ${formatPeriodoConsumo(p.periodo_inicio, p.periodo_fim)}`
                    : ''}
                  {p.mes_referencia ? ` · comp. ${formatMesReferencia(p.mes_referencia)}` : ''}
                  {p.avulso ? ' · extra' : ''}
                </option>
              ))}
            </select>
            {!pedidosDoMes.length && (
              <span className="kpi-sub">
                Nenhum pedido no mês da entrega. Crie um Pedido Mensal antes de planejar a rota.
              </span>
            )}
          </div>
          <div className="field">
            <label>Responsável pela entrega *</label>
            <select
              value={responsavelEntregaId}
              onChange={(e) => setResponsavelEntregaId(e.target.value)}
            >
              <option value="">Selecione (Logística)…</option>
              {logisticas.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nome} — {u.email}
                </option>
              ))}
            </select>
            {!logisticas.length && (
              <span className="kpi-sub">
                Nenhum usuário com perfil Logística. Cadastre em Configurações → Usuários.
              </span>
            )}
          </div>
          <div className="field">
            <label>Data da Entrega *</label>
            <input
              type="date"
              value={dataEntrega}
              onChange={(e) => {
                setDataEntrega(e.target.value)
                setError(null)
              }}
            />
            {entregaForaLetivo && (
              <span className="kpi-sub" style={{ color: 'var(--color-danger, #c62828)' }}>
                {entregaForaLetivo}
              </span>
            )}
            {!entregaForaLetivo && diasLetivos.size > 0 && (
              <span className="kpi-sub">Somente dias letivos do Calendário.</span>
            )}
          </div>
          <div className="field">
            <label>Período início</label>
            <input
              type="date"
              value={periodoInicio}
              onChange={(e) => {
                setPeriodoInicio(e.target.value)
                setError(null)
              }}
            />
          </div>
          <div className="field">
            <label>Período fim</label>
            <input
              type="date"
              value={periodoFim}
              onChange={(e) => {
                setPeriodoFim(e.target.value)
                setError(null)
              }}
            />
          </div>
          {pedidoSelecionado?.periodo_inicio && pedidoSelecionado?.periodo_fim && (
            <p className="kpi-sub" style={{ gridColumn: '1 / -1' }}>
              Período do pedido {pedidoSelecionado.codigo}:{' '}
              {formatPeriodoConsumo(pedidoSelecionado.periodo_inicio, pedidoSelecionado.periodo_fim)}.
              As quantidades vêm do cardápio × matrículas no intervalo; o saldo cobre só o consumo
              pedido nesse período.
            </p>
          )}
          {periodoPlanejamentoForaDoPedido && (
            <p className="kpi-sub" style={{ gridColumn: '1 / -1', color: 'var(--color-danger, #c62828)' }}>
              O período do planejamento é maior que o do pedido selecionado. A necessidade do cardápio
              pode exceder o saldo disponível — alinhe as datas ou escolha outro pedido.
            </p>
          )}
        </div>

        <div style={{ marginTop: 16 }}>
          <strong>Escolas na sequência</strong>
          {!rotaId ? (
            <p className="kpi-sub" style={{ marginTop: 8 }}>
              Selecione uma rota para carregar as escolas vinculadas (ordem alfabética; use ↑↓ para
              ajustar).
            </p>
          ) : !draftSchools.length ? (
            <p className="kpi-sub" style={{ marginTop: 8 }}>
              Nenhuma escola vinculada a esta rota em Configurações → Rotas.
            </p>
          ) : (
            <p className="kpi-sub" style={{ marginTop: 8, marginBottom: 12 }}>
              {draftSchools.length} escola(s) da rota — ordem alfabética; alimentos e quantidades
              carregados do cardápio (per capita × alunos).
              {carregandoItens ? ' Carregando itens…' : ''}
            </p>
          )}
          {rotaId && draftSchools.length > 0 && itensComFracaoEmbalagem.length > 0 && (
            <div className="alert alert-warning" style={{ marginBottom: 12 }} role="status">
              <div style={{ marginBottom: 8 }}>
                {itensComFracaoEmbalagem.length} item(ns) com fração de embalagem — arredonde por
                escola (pacote fechado). Padrão sugerido: para mais (↑). Após salvar, o pedido
                fica aguardando validação da Nutricionista ISEN.
                {itensComFracaoEmbalagem.length <= 8
                  ? ` ${itensComFracaoEmbalagem.join(', ')}.`
                  : ` ${itensComFracaoEmbalagem.slice(0, 8).join(', ')} (+${itensComFracaoEmbalagem.length - 8}).`}
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setDraftSchools((prev) =>
                    prev.map((s) => ({
                      ...s,
                      itens: s.itens.map((row) => {
                        const alimento = alimentos.find((a) => a.id === row.alimento_id)
                        const produto = produtoDoAlimento(alimento, produtoById)
                        return aplicarArredondamentoNaLinha(row, produto, 'mais')
                      }),
                    })),
                  )
                }}
              >
                Arredondar todos para mais (↑)
              </button>
            </div>
          )}
          {rotaId && draftSchools.length > 0 && escolasSemItens.length > 0 && (
            <div className="alert alert-warning" style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}>
                {escolasSemItens.length} escola(s) sem itens
                {filtroSemItens ? ' — mostrando só estas.' : '.'} Não é possível salvar enquanto
                houver escolas vazias.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setFiltroSemItens((v) => !v)}
                >
                  {filtroSemItens ? 'Ver todas' : 'Ver só sem itens'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={removerTodasSemItens}
                >
                  Remover todas sem itens
                </button>
              </div>
            </div>
          )}
          {rotaId && escolasDisponiveis.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                alignItems: 'flex-end',
                marginBottom: 12,
              }}
            >
              <div className="field" style={{ flex: 1, minWidth: 200 }}>
                <label>Readicionar escola removida</label>
                <select
                  value={escolaParaAdd}
                  onChange={(e) => setEscolaParaAdd(e.target.value)}
                >
                  <option value="">Selecione</option>
                  {escolasDisponiveis.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nome}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={!escolaParaAdd}
                onClick={addSchool}
              >
                Adicionar
              </button>
            </div>
          )}

          {filtroSemItens && draftVisivel.length === 0 && draftSchools.length > 0 && (
            <p className="kpi-sub" style={{ marginBottom: 12 }}>
              Nenhuma escola sem itens no momento.
            </p>
          )}

          {draftVisivel.map((school) => {
            const idx = draftSchools.findIndex((s) => s.key === school.key)
            const semItens = escolaSemItensPreenchidos(school)
            return (
            <div key={school.key} className="card" style={{ marginBottom: 12, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div>
                  <strong>
                    {idx + 1}. {escolaNome(school.escola_id)}
                  </strong>
                  <span className="kpi-sub" style={{ marginLeft: 8 }}>
                    {formatNumber(escolaTotalAlunos(school.escola_id), 0)} aluno(s)
                  </span>
                  {semItens && (
                    <span className="badge badge-atencao" style={{ marginLeft: 8 }}>
                      Sem itens
                    </span>
                  )}
                </div>
                <div className="table-actions">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={idx <= 0}
                    onClick={() => moveSchool(school.key, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={idx < 0 || idx === draftSchools.length - 1}
                    onClick={() => moveSchool(school.key, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={
                      sugerindoKey === school.key ||
                      !periodoInicio ||
                      !periodoFim ||
                      periodoInicio > periodoFim
                    }
                    title={
                      !periodoInicio || !periodoFim || periodoInicio > periodoFim
                        ? 'Informe o período início e fim'
                        : undefined
                    }
                    onClick={() => void sugerirItens(school.key)}
                  >
                    {sugerindoKey === school.key ? 'Sugerindo…' : 'Sugerir itens'}
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeSchool(school.key)}>
                    Remover
                  </button>
                </div>
              </div>

              <div className="table-wrap" style={{ marginTop: 8 }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="col-compact">Categoria</th>
                      <th>Produto</th>
                      <th className="col-num">Quantidade (kg)</th>
                      <th className="col-num">Qnt produto pedido (un)</th>
                      <th className="col-num">Ajuste embalagem</th>
                      <th className="col-actions">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!school.itens.length && (carregandoItens || sugerindoKey === school.key) && (
                      <tr>
                        <td colSpan={6} className="kpi-sub">
                          Carregando alimentos do cardápio…
                        </td>
                      </tr>
                    )}
                    {!school.itens.length &&
                      !carregandoItens &&
                      sugerindoKey !== school.key && (
                        <tr>
                          <td colSpan={6} className="kpi-sub">
                            Nenhum item do cardápio para o período. Use &quot;Sugerir itens&quot; ou
                            verifique Calendário, Cardápio e matrículas.
                          </td>
                        </tr>
                      )}
                    {school.itens.map((row) => {
                      const alimento = alimentos.find((a) => a.id === row.alimento_id)
                      const categoria = alimento?.categoria || '—'
                      const produto = produtoDoAlimento(alimento, produtoById)
                      const ajuste = formatAjusteEmbalagemSugerido(row.quantidade, produto)
                      return (
                        <tr key={row.key}>
                          <td className="col-compact">{categoria}</td>
                          <td>
                            <select
                              value={row.alimento_id}
                              onChange={(e) =>
                                updateItem(school.key, row.key, { alimento_id: e.target.value })
                              }
                            >
                              <option value="">Selecione</option>
                              {alimentosDoCardapio.map((a) => {
                                const p = produtoDoAlimento(a, produtoById)
                                return (
                                  <option key={a.id} value={a.id}>
                                    {p?.nome || a.nome}
                                  </option>
                                )
                              })}
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              min={0.001}
                              step="any"
                              title="Calculada: per capita PNAE × alunos elegíveis por turno/refeição no período"
                              value={row.quantidade}
                              onChange={(e) =>
                                updateItem(school.key, row.key, { quantidade: e.target.value })
                              }
                            />
                          </td>
                          <td className="col-num kpi-sub">
                            {formatQtdProdutoUn(row.quantidade, produto)}
                          </td>
                          <td
                            className="col-num kpi-sub"
                            title={ajuste ? 'Arredonde para embalagem fechada (↓ menos / ↑ mais)' : undefined}
                          >
                            {ajuste ?? '—'}
                            {ajuste && (
                              <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  title="Arredondar para menos"
                                  onClick={() =>
                                    setDraftSchools((prev) =>
                                      prev.map((s) =>
                                        s.key !== school.key
                                          ? s
                                          : {
                                              ...s,
                                              itens: s.itens.map((it) =>
                                                it.key !== row.key
                                                  ? it
                                                  : aplicarArredondamentoNaLinha(it, produto, 'menos'),
                                              ),
                                            },
                                      ),
                                    )
                                  }
                                >
                                  ↓
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  title="Arredondar para mais"
                                  onClick={() =>
                                    setDraftSchools((prev) =>
                                      prev.map((s) =>
                                        s.key !== school.key
                                          ? s
                                          : {
                                              ...s,
                                              itens: s.itens.map((it) =>
                                                it.key !== row.key
                                                  ? it
                                                  : aplicarArredondamentoNaLinha(it, produto, 'mais'),
                                              ),
                                            },
                                      ),
                                    )
                                  }
                                >
                                  ↑
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="col-actions">
                            <IconButton
                              variant="delete"
                              label="Remover item"
                              disabled={school.itens.length <= 1}
                              onClick={() => removeItem(school.key, row.key)}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => addItem(school.key)}>
                  + Item
                </button>
                <span className="kpi-sub">
                  {formatNumber(
                    school.itens.filter((i) => i.alimento_id && Number(i.quantidade) > 0).length,
                    0,
                  )}{' '}
                  item(ns)
                </span>
              </div>
            </div>
            )
          })}
        </div>
      </Modal>

      <Modal
        open={!!detail}
        title={
          detail
            ? `${detail.rota?.nome || 'Rota'} · ${formatDate(detail.data_entrega)}`
            : 'Detalhe'
        }
        onClose={() => {
          setDetail(null)
          setExpandedSchool(null)
        }}
        footer={
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setDetail(null)
              setExpandedSchool(null)
            }}
          >
            Fechar
          </button>
        }
      >
        {detail && (
          <>
            <p className="kpi-sub" style={{ marginBottom: 12 }}>
              Status: <span className={statusBadgeClass(detail.status)}>{statusLabel(detail.status)}</span>
              {' · '}
              Data prevista: {formatDate(detail.data_entrega)}
              {detail.compra?.codigo ? ` · Pedido ${detail.compra.codigo}` : ''}
              {detail.responsavel_entrega?.nome
                ? ` · Responsável: ${detail.responsavel_entrega.nome}`
                : ''}
            </p>
            {(detail.escolas || []).map((esc) => {
              const open = expandedSchool === esc.id
              return (
                <div key={esc.id} className="card" style={{ marginBottom: 8, padding: 12 }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ width: '100%', textAlign: 'left', padding: 0 }}
                    onClick={() => setExpandedSchool(open ? null : esc.id)}
                  >
                    <strong>
                      {esc.ordem}. {esc.escola?.nome || '—'}
                    </strong>
                    <span className="kpi-sub" style={{ marginLeft: 8 }}>
                      {esc.itens?.length || 0} item(ns) · {open ? '▼' : '▶'}
                    </span>
                  </button>
                  {open && (
                    <div className="table-wrap" style={{ marginTop: 8 }}>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th className="col-compact">Categoria</th>
                            <th>Produto</th>
                            <th className="col-num">Qnt produto pedido (un)</th>
                            <th className="col-num">Ajuste embalagem</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(esc.itens || []).map((it) => {
                            const alimento =
                              it.alimento ?? alimentos.find((a) => a.id === it.alimento_id)
                            const categoria = alimento?.categoria || '—'
                            const produto = produtoDoAlimento(alimento, produtoById)
                            const ajuste = formatAjusteEmbalagemSugerido(it.quantidade, produto)
                            return (
                              <tr key={it.id}>
                                <td className="col-compact">{categoria}</td>
                                <td>{produto?.nome || alimento?.nome || it.alimento_id}</td>
                                <td className="col-num">
                                  {formatQtdProdutoUn(it.quantidade, produto)}
                                </td>
                                <td className="col-num kpi-sub">{ajuste ?? '—'}</td>
                              </tr>
                            )
                          })}
                          {!esc.itens?.length && (
                            <tr>
                              <td colSpan={4} className="kpi-sub">
                                Sem itens.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )
            })}
          </>
        )}
      </Modal>
    </div>
  )
}
